import { useMemo } from 'react';

import {
  WorkflowEdgesData,
  WorkflowNodeId,
  WorkflowNodesData,
  WorkflowStartNodeData,
} from '../types';

const createMapOfWorkflowEdges = (workflow: WorkflowEdgesData) =>
  workflow.edges.reduce<Record<WorkflowNodeId, WorkflowNodeId[]>>(
    (previousValue, edge) => ({
      ...previousValue,
      [edge.srcWorkflowNodeId]: [
        ...(previousValue[edge.srcWorkflowNodeId] || []),
        edge.destWorkflowNodeId,
      ],
    }),
    {},
  );

const getAttachedWorkflowNodeIds = (
  workflow: WorkflowNodesData & WorkflowEdgesData & WorkflowStartNodeData,
) => {
  // Perform a depth-first search to find all nodes descendent from the start node
  const nodeEdgeMap = createMapOfWorkflowEdges(workflow);
  const visitedNodeIds = new Set<WorkflowNodeId>();
  const depthFirstSearchStack = [workflow.startNode?.id];
  while (depthFirstSearchStack.length > 0) {
    const currentNodeId = depthFirstSearchStack.pop();
    if (currentNodeId !== undefined && !visitedNodeIds.has(currentNodeId)) {
      visitedNodeIds.add(currentNodeId);
      nodeEdgeMap[currentNodeId]?.forEach((childNodeId) => depthFirstSearchStack.push(childNodeId));
    }
  }
  return [...visitedNodeIds];
};

/**
 * Generates an array of workflow nodes that are descendent of the start node
 */
export const useAttachedWorkflowNodeIds = (
  workflow: WorkflowNodesData & WorkflowEdgesData & WorkflowStartNodeData,
) =>
  useMemo(
    () => getAttachedWorkflowNodeIds(workflow),
    [workflow.allNodes, workflow.edges, workflow.startNode],
  );
